import { environment } from "../environment";

// Helper function to get auth token
const getToken = () => {
  return localStorage.getItem("token");
};

// Helper function to make authenticated API calls
const parseApiErrorMessage = async (response) => {
  const fallback = `API call failed: ${response.status} ${response.statusText}`;

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    return fallback;
  }

  if (!payload) {
    return fallback;
  }

  // FastAPI commonly returns "detail" as a string or a validation array.
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    const firstIssue = payload.detail[0];
    if (typeof firstIssue === "string") {
      return firstIssue;
    }
    if (firstIssue && typeof firstIssue.msg === "string") {
      return firstIssue.msg;
    }
  }

  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  return fallback;
};

const apiCall = async (endpoint, options = {}) => {
  const url = `${environment.apiUrl}${endpoint}`;
  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const message = await parseApiErrorMessage(response);
    throw new Error(message);
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

// API functions for the new endpoints
export const api = {
  // EMR Types
  getEMRTypes: () => apiCall("/emr-types/"),
  getEMRType: (uuid) => apiCall(`/emr-types/${uuid}/`),

  // EMR Type Field Responses
  getEMRTypeFieldResponses: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.emr_type_id) params.set("emr_type_id", filters.emr_type_id);
    if (filters.field_name) params.set("field_name", filters.field_name);
    if (filters.response_value) params.set("response_value", filters.response_value);
    const queryString = params.toString();
    return apiCall(`/emr-type-field-responses${queryString ? `?${queryString}` : ""}`);
  },
  getEMRTypeFieldResponse: (id) => apiCall(`/emr-type-field-responses/${id}`),
  createEMRTypeFieldResponse: (payload) =>
    apiCall("/emr-type-field-responses", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateEMRTypeFieldResponse: (id, payload) =>
    apiCall(`/emr-type-field-responses/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteEMRTypeFieldResponse: (id) =>
    apiCall(`/emr-type-field-responses/${id}`, {
      method: "DELETE",
    }),

  // Documentation Methods
  getDocumentationMethods: () => apiCall("/documentation-methods/"),
  getDocumentationMethod: (uuid) => apiCall(`/documentation-methods/${uuid}/`),

  // Coping Skills
  getCopingSkills: () => apiCall("/coping-skills/"),
  getCopingSkill: (uuid) => apiCall(`/coping-skills/${uuid}/`),

  // Clinical Specialties
  getClinicalSpecialties: () => apiCall("/clinical-specialties/"),
  getClinicalSpecialty: (uuid) => apiCall(`/clinical-specialties/${uuid}/`),

  // Users
  getUsers: () => apiCall("/users"),
  getUser: (id) => apiCall(`/users/${id}`),
  updateUser: (id, userData) =>
    apiCall(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    }),

  // Current user (me)
  getMe: () => apiCall("/me"),
  updateMe: (userData) =>
    apiCall("/me", {
      method: "PUT",
      body: JSON.stringify(userData),
    }),
};

// Type Writing options (these are predefined strings, not from API)
export const typeWritingOptions = [
"Soft", 
"Gentle", 
"Empathetic",
"Supportive",
"Encouraging", 
"Calm", 
"Reassuring",
"Hopeful", 
"Reflective",
"Compassionate",
"Uplifting", 
"Understanding",
"Patient",
"Non-judgmental",
"Inspiring", 
"Affirming",
"Grounding", 
"Validating",
"Mindful", 
"Soothing"
];

export default api;
